"use client";

import { Button } from "@/components/atoms/buttons";
import { toast } from "@/components/atoms/toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useCart } from "@/hooks/useCart";
import { useCustomer } from "@/hooks/useCustomer";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { normalizeDeliverySchedule } from "@/lib/fixed-delivery-schedule";
import { sdk } from "@/lib/medusa/client";
import { cn, getAddressAttributes, normalizeDate } from "@/lib/utils";
import { useCompanyAddresses, useCompanyDetails } from "@/queries/company";
import { useCheckoutStore } from "@/stores/checkout-store";
import { DeliveryAddress, OrderWorkflowResponse } from "@/types";
import { Loader2 } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTransition } from "react";
import { resolveCheckoutFulfillment } from "@/lib/checkout-delivery";
import { resolveCartAddresses } from "@/lib/cart-addresses";
import { hasInvalidFulfillmentSelectionMessage, isInvalidFulfillmentSelectionError } from "@/lib/order-workflow-error";
import {
  BILLING_ADDRESS_REQUIREMENT_MESSAGE,
  isBillingAddressEligibleForAccountType,
} from "@/lib/billing-address-eligibility";
import {
  CheckoutAttemptStage,
  clearCheckoutAttempt,
  createFailureEvent,
  getOrCreateCheckoutAttemptId,
  linkCheckoutAttemptToCart,
  reportCheckoutAttempt,
} from "@/lib/checkout-attempt";

type CompleteCartProps = {
  locale: Locales;
  className?: string;
};

const CompleteCart = ({ locale, className }: CompleteCartProps) => {
  const router = useRouter();
  const isMobile = useIsMobile();
  const { cart, refreshCart, setCart } = useCart();
  const { customer } = useCustomer();
  const {
    accountType,
    billingAddress: billingAddressId,
    promoCode,
    date,
    deliveryAddress: deliveryAddressId,
    deliveryDate,
    wantsDelivery,
    recoveryDate,
    email,
    firstName,
    lastName,
    message,
    phone,
    title,
    eventType,
    setJustCompletedOrder,
    productCustomizations,
  } = useCheckoutStore();
  const [isPending, startTransition] = useTransition();
  const isCompanyAccountSelected = accountType === "company";
  const { data: companyDetails, isLoading: isCompanyLoading } = useCompanyDetails(
    customer?.company_name || "",
    customer?.id || "anonymous"
  );
  const { data: companyAddresses, isLoading: isCompanyAddressesLoading } = useCompanyAddresses(
    isCompanyAccountSelected ? companyDetails?.id : ""
  );
  const deliveryAddresses: DeliveryAddress[] | undefined = isCompanyAccountSelected
    ? companyAddresses
    : customer?.addresses;

  const { deliveryAddress: selectedDeliveryAddress, deliveryAddressMetadata } = getAddressAttributes(
    deliveryAddresses,
    undefined,
    deliveryAddressId
  );
  const { billingAddress: selectedBillingAddress, billingAddressMetadata } = getAddressAttributes(
    customer?.addresses,
    billingAddressId
  );
  const isAddressLoading =
    customer === undefined || (isCompanyAccountSelected && (isCompanyLoading || isCompanyAddressesLoading));
  const normalizedDeliveryAddressMetadata = selectedDeliveryAddress
    ? normalizeDeliverySchedule(deliveryAddressMetadata)
    : deliveryAddressMetadata;
  const { shippingAddress, billingAddress } = resolveCartAddresses({
    shippingAddress: selectedDeliveryAddress && {
      ...selectedDeliveryAddress,
      country_code: selectedDeliveryAddress.country_code || deliveryAddressMetadata.country_code,
      metadata: normalizedDeliveryAddressMetadata,
    },
    billingAddress: selectedBillingAddress && {
      ...selectedBillingAddress,
      country_code: selectedBillingAddress.country_code || billingAddressMetadata.country_code,
    },
    firstName: firstName || customer?.first_name,
    lastName: lastName || customer?.last_name,
    phone: phone || customer?.phone,
    defaultCountryCode: "be",
  });
  const fulfillment = resolveCheckoutFulfillment(wantsDelivery);
  const isShippingAddressRequired = fulfillment.requiresShippingAddress;
  const isSelectedBillingAddressEligible =
    !!selectedBillingAddress &&
    isBillingAddressEligibleForAccountType(selectedBillingAddress, isCompanyAccountSelected ? "company" : "individual");
  const isSubmitDisabled = isPending || isAddressLoading || (isShippingAddressRequired && !selectedDeliveryAddress);

  const showOutdatedFulfillmentError = () =>
    toast.error({
      title: __("Your checkout needs to be refreshed.", locale),
      description: __("Refresh the page and select your delivery option again.", locale),
      button: {
        label: __("Refresh checkout", locale),
        onClick: () => window.location.reload(),
      },
    });

  const handleClick = async () => {
    if (isAddressLoading) {
      toast.warning({ title: __("Addresses are still loading. Please try again in a moment.", locale) });
      return;
    }

    if (isShippingAddressRequired && !selectedDeliveryAddress) {
      toast.error({ title: __("Please select a delivery address", locale) });
      return;
    }

    if (!isSelectedBillingAddressEligible) {
      toast.error({ title: __(BILLING_ADDRESS_REQUIREMENT_MESSAGE, locale) });
      return;
    }

    if (!cart || !cart.id || !cart.items || !cart.items[0]) {
      toast.error({
        title: __("No products in cart added", locale),
        description: __("Please add products to cart before proceeding.", locale),
      });
      return;
    }

    // Only pass productCustomizations for products which are in cart
    const filteredCustomizations = Object.fromEntries(
      Object.entries(productCustomizations).filter(([medusaId]) =>
        cart?.items?.some((item) => item.product_id === medusaId)
      )
    );

    const attemptId = getOrCreateCheckoutAttemptId(cart.id);
    let trackedCartId = cart.id;
    let currentStage: CheckoutAttemptStage = "transfer_cart";
    let createdOrderId: string | undefined;

    const reportProgress = (stage: CheckoutAttemptStage) => {
      currentStage = stage;
      void reportCheckoutAttempt({
        attempt_id: attemptId,
        cart_id: trackedCartId,
        event: "progress",
        stage,
        ...(createdOrderId && { order_id: createdOrderId }),
      });
    };

    startTransition(async () => {
      try {
        // Transfer cart
        reportProgress("transfer_cart");
        const { cart: newCart } = await sdk.store.cart.transferCart(cart.id);
        setCart(newCart);
        const cartId = newCart.id;
        trackedCartId = cartId;
        linkCheckoutAttemptToCart(attemptId, cartId);

        // Update cart metadata
        reportProgress("update_cart");
        const updatedCart = await sdk.store.cart.update(cartId, {
          ...(selectedDeliveryAddress && {
            shipping_address: shippingAddress,
          }),
          billing_address: billingAddress,
          metadata: {
            checkout_attempt_id: attemptId,
            checkout_cart_id: cartId,
            lang: locale,
            request_type: "quote",
            account_type: accountType,
            promo_code: promoCode,
            eventDates: date,
            delivery_date: normalizeDate(deliveryDate),
            wants_delivery: fulfillment.wantsDelivery,
            recovery_date: normalizeDate(recoveryDate),
            email,
            first_name: firstName,
            last_name: lastName,
            phone,
            title,
            event_type: eventType,
            event_message: message,
            wants_to_recover_items: fulfillment.wantsRecovery,
            shipping_address_message: deliveryAddressMetadata.message || "",
            billing_address_message: billingAddressMetadata.message || "",
            delivery_location_type: deliveryAddressMetadata.delivery_location_type || "",
            delivery_floor: deliveryAddressMetadata.delivery_floor || "",
            delivery_has_elevator: deliveryAddressMetadata.delivery_has_elevator,
            delivery_instructions:
              deliveryAddressMetadata.delivery_instructions || deliveryAddressMetadata.message || "",
            delivery_elevator_dimensions: deliveryAddressMetadata.delivery_elevator_dimensions || "",
            delivery_elevator_door_width: deliveryAddressMetadata.delivery_elevator_door_width || "",
            delivery_has_stairs: deliveryAddressMetadata.delivery_has_stairs,
            delivery_stairs_count: deliveryAddressMetadata.delivery_stairs_count || "",
            delivery_has_ground_level_access: deliveryAddressMetadata.delivery_has_ground_level_access,
            delivery_access_details: deliveryAddressMetadata.delivery_access_details || "",
            delivery_parking_type: deliveryAddressMetadata.delivery_parking_type || "",
            delivery_parking_details: deliveryAddressMetadata.delivery_parking_details || "",
            delivery_schedule_type: normalizedDeliveryAddressMetadata.delivery_schedule_type || "",
            delivery_time_slot: normalizedDeliveryAddressMetadata.delivery_time_slot || "",
            delivery_schedule_details: normalizedDeliveryAddressMetadata.delivery_schedule_details || "",
            delivery_contact_name: deliveryAddressMetadata.delivery_contact_name || "",
            delivery_contact_phone: deliveryAddressMetadata.delivery_contact_phone || "",
            ...(accountType === "company" && companyDetails?.id && { company_id: companyDetails.id }),
            ...(productCustomizations && { productCustomizations: filteredCustomizations }),
          },
        });

        // Step 1: Get available shipping options
        reportProgress("shipping_options");
        const { shipping_options } = await sdk.store.fulfillment.listCartOptions({
          cart_id: cartId,
        });

        // Step 2: Add shipping method if one isn't already selected and shipping options exist
        if (shipping_options.length > 0 && !updatedCart.cart.shipping_methods?.length) {
          const selectedShippingOption = shipping_options[0];

          reportProgress("shipping_method");
          await sdk.store.cart.addShippingMethod(cartId, {
            option_id: selectedShippingOption.id,
          });
        }

        // Step 3: Get the updated cart with shipping method
        const { cart: cartWithShipping } = await sdk.store.cart.retrieve(cartId);

        // Step 4: Initiate payment session ( Using default for manual payments )
        reportProgress("payment");
        await sdk.store.payment.initiatePaymentSession(cartWithShipping, {
          provider_id: "pp_system_default",
        });

        // Step 5: Get the updated cart with payment session
        const { cart: cartWithPayment } = await sdk.store.cart.retrieve(cartId);

        // Step 6: Complete the cart
        reportProgress("complete_cart");
        const res = await sdk.store.cart.complete(cartWithPayment.id);

        if (res.type === "cart" && res.cart) {
          console.error("Cart completion failed for cart : ", res.cart, "Due to : ", res.error);
          await reportCheckoutAttempt({
            ...createFailureEvent(
              {
                attempt_id: attemptId,
                cart_id: trackedCartId,
                stage: "complete_cart",
              },
              res.error
            ),
            ...(hasInvalidFulfillmentSelectionMessage(res.error) && {
              failure_code: "fulfillment_outdated" as const,
            }),
          });
          if (hasInvalidFulfillmentSelectionMessage(res.error)) {
            showOutdatedFulfillmentError();
            return;
          }

          toast.error({
            title: __("An error occurred while placing your order", locale),
            description: __("Please try again later", locale),
          });
          return;
        }

        createdOrderId = res.type === "order" ? res.order.id : undefined;
        if (!createdOrderId) {
          throw new Error("Order completion returned no order identifier");
        }

        await reportCheckoutAttempt({
          attempt_id: attemptId,
          cart_id: trackedCartId,
          event: "order_received",
          stage: "order_workflow",
          order_id: createdOrderId,
        });

        reportProgress("order_workflow");
        let workflowResponse: OrderWorkflowResponse | undefined;
        try {
          workflowResponse = await sdk.client.fetch<OrderWorkflowResponse>(`store/order/place-order-workflow`, {
            method: "POST",
            body: {
              id: createdOrderId,
            },
          });
        } catch (error) {
          console.error("Post-order workflow failed:", error);
          await reportCheckoutAttempt({
            ...createFailureEvent(
              {
                attempt_id: attemptId,
                cart_id: trackedCartId,
                stage: "order_workflow",
              },
              error
            ),
            order_id: createdOrderId,
            ...(isInvalidFulfillmentSelectionError(error) && {
              failure_code: "fulfillment_outdated" as const,
            }),
          });

          toast.warning({
            title: __("Your order has been received.", locale),
            description: __("A technical issue occurred after receipt. Please contact us at rent@festi.be", locale),
            button: {
              label: __("Contact us", locale),
              onClick: () => router.push(getPermalink(locale, "contact")),
            },
          });

          setJustCompletedOrder(true);
          refreshCart();
          clearCheckoutAttempt(attemptId);
          router.replace(`${getPermalink(locale, "checkout", "thank-you")}/${createdOrderId}?error=workflow`);
          return;
        }

        const { status } = workflowResponse;

        if (status === 500) {
          await reportCheckoutAttempt({
            ...createFailureEvent({
              attempt_id: attemptId,
              cart_id: trackedCartId,
              stage: "order_workflow",
            }),
            order_id: createdOrderId,
          });
          toast.warning({
            title: __("An error has occurred.", locale),
            description: __("Please contact us at rent@festi.be", locale),
            button: {
              label: __("Contact us", locale),
              onClick: () => router.push(getPermalink(locale, "contact")),
            },
          });
        } else {
          await reportCheckoutAttempt({
            attempt_id: attemptId,
            cart_id: trackedCartId,
            event: "completed",
            stage: "completed",
            order_id: createdOrderId,
          });
          toast.success({ title: __("Your order has been placed successfully", locale) });
        }

        setJustCompletedOrder(true);
        refreshCart();
        clearCheckoutAttempt(attemptId);

        const query = status === 500 ? "?error=workflow" : "";

        router.replace(`${getPermalink(locale, "checkout", "thank-you")}/${createdOrderId}${query}`);
      } catch (error) {
        console.error("Error completing cart:", error);
        await reportCheckoutAttempt({
          ...createFailureEvent(
            {
              attempt_id: attemptId,
              cart_id: trackedCartId,
              stage: currentStage,
            },
            error
          ),
          ...(createdOrderId && { order_id: createdOrderId }),
          ...(isInvalidFulfillmentSelectionError(error) && {
            failure_code: "fulfillment_outdated" as const,
          }),
        });
        toast.error({
          title: __("An error occurred while validating your order", locale),
          description: __("Please try again later.", locale),
        });
      }
    });
  };

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {!isAddressLoading && !isSelectedBillingAddressEligible && (
        <p role="alert" className="text-pink-primary max-w-md text-sm font-medium">
          {__(BILLING_ADDRESS_REQUIREMENT_MESSAGE, locale)}
        </p>
      )}
      <Button
        disabled={isSubmitDisabled}
        onClick={handleClick}
        className="h-12 w-full transition-transform duration-150 ease-in-out active:scale-95 min-sm:h-[60px]"
      >
        {isMobile ? (
          <>{isPending ? <Loader2 className="mr-2 animate-spin" /> : __("Validate my order", locale)}</>
        ) : (
          <>
            {isPending && <Loader2 className="mr-2 animate-spin" />}
            {__("Validate my quote", locale)}
          </>
        )}
      </Button>
    </div>
  );
};

export default CompleteCart;
